import {observable} from "@nx-js/observer-util";
import {Services} from "../Services";
import {Network} from "../../network/Network";
import {ICommunityModel} from "../../models/community/ICommunityModel";
import {CommunityArticleService} from "./articles/CommunityArticleService";
import {CommunityAgentChatService} from "./agent/chat/CommunityAgentChatService";
import {INetworkComponent} from "../../network/types/INetworkComponent";
import {HttpStatus} from "../../network/status/HttpStatus";
import {ICommunityLandingIntent} from "../../models/community/ICommunityLandingIntent";
import {ICommunityStartChecklistModel} from "../../models/community/ICommunityStartChecklistModel";
import {ProductType} from "../../models/product/ProductType";
import {Resources} from "../../resources/Resources";
import {CommunityFilesService} from "./files/CommunityFilesService";
import {CommunityLibraryService} from "./library/CommunityLibraryService";
import {CommunityImagesService} from "./images/CommunityImagesService";
import {CommunityAltService} from "./alt/CommunityAltService";
import {CommunityType} from "../../models/community/types/CommunityType";
import {ErrorCode} from "../../network/status/error/ErrorCode";
import {NetworkSocket} from "../../network/socket/NetworkSocket";
import {WebSocketCommunityEventName} from "../../network/socket/names/WebSocketCommunityEventName";
import {
    SedestralMachine
} from "../../sedestral/framework/machine/SedestralMachine";
import {
    ICommunityPartnerBlogModel,
} from "../../models/community/partner/ICommunityPartnerBlogModel";
import {CommunityArticleAutopilotService} from "./autopilot/CommunityArticleAutopilotService";
import {ICommunityAdminModel} from "../../models/community/ICommunityAdminModel";
import {CommunityWritingStyleService} from "./writingStyle/CommunityWritingStyleService";
import {ICommunityCreateStep0Model} from "../../models/community/ICommunityCreateStep0Model";
import {ICommunityCreateStep1Model} from "../../models/community/ICommunityCreateStep1Model";
import {ICommunityOptions} from "../../products/panel/components/components/community/types/ICommunityOptions";
import {CommunityKeywordsService} from "./keywords/CommunityKeywordsService";
import {CommunityDocumentsService} from "./documents/CommunityDocumentsService";
import {CommunityBrandFactsService} from "./brand/CommunityBrandFactsService";
import {CommunityClustersService} from "./keywords/CommunityClustersService";
import {CommunityGscService} from "./gsc/CommunityGscService";
import {CommunityBusinessService} from "./business/CommunityBusinessService";
import {CommunityEstablishmentService} from "./establishment/CommunityEstablishmentService";
import {CommunityIndexationService} from "./indexation/CommunityIndexationService";
import {ICommunityPartnerBlogColumnIncoming} from "../../models/community/partner/ICommunityPartnerBlogColumnIncoming";
import {ICommunityPartnerBlogColumnOutgoing} from "../../models/community/partner/ICommunityPartnerBlogColumnOutgoing";
import {ICommunityOverviewModel} from "../../models/community/ICommunityOverviewModel";
import {CommunitySeoAuditService} from "./audit/CommunitySeoAuditService";
import {CommunitySeoNewPageTrackingService} from "./newPageTracking/CommunitySeoNewPageTrackingService";
import {CommunityArticleOptimizationBatchService} from "./optimizationBatch/CommunityArticleOptimizationBatchService";
import {CommunityCtaService} from "./cta/CommunityCtaService";
import {ICommunityPartnerCategoryModel} from "../../models/community/partner/ICommunityPartnerCategoryModel";
import {ICommunitySettings, ICommunitySettingsResponse} from "../../models/community/ICommunitySettings";
import {CommunityAgentService} from "./agent/CommunityAgentService";
import {CommunityBacklinkService} from "./backlink/CommunityBacklinkService";
import {CommunityBacklinkCampaignService} from "./backlink/CommunityBacklinkCampaignService";
import {CommunityCalendarService} from "./calendar/CommunityCalendarService";
import {CommunityReportTemplateService} from "./report/CommunityReportTemplateService";
import {CommunityReportService} from "./report/CommunityReportService";
import {ICommunityDemoRoiModel} from "../../models/community/ICommunityDemoRoiModel";
import {ICommunityPartnerMetafieldDefinition} from "../../models/community/partner/ICommunityPartnerMetafieldDefinition";
import {ICommunityPartnerMetafieldConfigIncoming} from "../../models/community/partner/ICommunityPartnerMetafieldConfigIncoming";
import {ICommunityPartnerMetafieldConfigOutgoing} from "../../models/community/partner/ICommunityPartnerMetafieldConfigOutgoing";
import {ICommunityPartnerMetaobjectField} from "../../models/community/partner/ICommunityPartnerMetaobjectField";
import {ICommunityAdminUpdateOutgoing} from "../../models/community/ICommunityAdminUpdateOutgoing";
import {ICommunityWelcomeStepIncoming} from "../../models/community/welcome/ICommunityWelcomeStepIncoming";
import {arrayRemove} from "../../sedestral/framework/utilities/ArrayRemove";

export class CommunityService {

    public static communities: ICommunityModel[] = observable([]);

    public static welcomeStepEvents: ((event: ICommunityWelcomeStepIncoming) => void)[] = [];

    public static dispose(): void {
        this.welcomeStepEvents = [];
        CommunityArticleService.dispose();
        CommunityFilesService.dispose();
        CommunityArticleAutopilotService.dispose();
        CommunityKeywordsService.dispose();
        CommunityClustersService.dispose();
        CommunitySeoAuditService.dispose();
        CommunitySeoNewPageTrackingService.dispose();
        CommunityArticleOptimizationBatchService.dispose();
        CommunityCtaService.dispose();
        CommunityDocumentsService.dispose();
        CommunityBrandFactsService.dispose();
        CommunityWritingStyleService.dispose();
        CommunityGscService.dispose();
        CommunityBusinessService.dispose();
        CommunityEstablishmentService.dispose();
        CommunityIndexationService.dispose();
        CommunityAgentService.dispose();
        CommunityBacklinkService.dispose();
        CommunityBacklinkCampaignService.dispose();
        CommunityCalendarService.dispose();
        CommunityReportTemplateService.dispose();
        CommunityReportService.dispose();
    }

    public static init(): void {
        CommunityArticleService.init();
        CommunityAgentChatService.init();
        CommunityFilesService.init();
        CommunityImagesService.init();
        CommunityAltService.init();
        CommunityArticleAutopilotService.init();
        CommunityKeywordsService.init();
        CommunityClustersService.init();
        CommunitySeoAuditService.init();
        CommunitySeoNewPageTrackingService.init();
        CommunityArticleOptimizationBatchService.init();
        CommunityCtaService.init();
        CommunityDocumentsService.init();
        CommunityBrandFactsService.init();
        CommunityWritingStyleService.init();
        CommunityGscService.init();
        CommunityBusinessService.init();
        CommunityEstablishmentService.init();
        CommunityIndexationService.init();
        CommunityLibraryService.init();
        CommunityAgentService.init();
        CommunityBacklinkService.init();
        CommunityBacklinkCampaignService.init();
        CommunityCalendarService.init();
        CommunityReportTemplateService.init();
        CommunityReportService.init();

        window["CommunityService"] = this;

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_UPDATE, (data) => {
            this.store(data);
        });

        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_UPDATE_ONBOARD, (event: ICommunityWelcomeStepIncoming) => {
            this.welcomeStepEvents.forEach(value => value(event));
        });
    }

    public static onWelcomeStep(component: INetworkComponent, func: (event: ICommunityWelcomeStepIncoming) => void) {
        this.welcomeStepEvents.push(func);
        component.onRemove(() => arrayRemove(this.welcomeStepEvents, func));
    }

    /**
     * http
     */

    public static async addLanguage(communityId: string, language: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.put(ProductType.COMMUNITY, "/community/addLanguage", {
            communityId: communityId,
            language: language
        }, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }

        return undefined;
    }

    public static async updateSendEmailNotificationRanking(communityId: string, sendEmailNotificationRanking: boolean): Promise<ICommunityModel> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/sendEmailNotificationRanking/update/${communityId}/${sendEmailNotificationRanking}`);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async landingConsume(communityId: string, component?: INetworkComponent): Promise<ICommunityLandingIntent> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/landing/consume/${communityId}`, {}, component);
        return request.status == HttpStatus.OK && request.data ? request.data : undefined;
    }

    public static async welcomeComplete(communityId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/welcome/complete/${communityId}`, {}, component);
        return request.status == HttpStatus.OK;
    }

    /**
     * Le rail et le suivi du composeur lisent le même état, souvent au même instant (fin de tour,
     * notification) : une lecture en cours est partagée, et son résultat sert quelques secondes.
     * Le serveur vérifie la permission par un appel au panel à chaque lecture — la doubler ne
     * dit rien de plus.
     */
    private static startChecklistInFlight: Map<string, Promise<ICommunityStartChecklistModel>> = new Map();
    private static startChecklistCache: Map<string, { time: number, checklist: ICommunityStartChecklistModel }> = new Map();
    private static startChecklistGeneration: Map<string, number> = new Map();
    private static readonly START_CHECKLIST_CACHE_MILLIS = 3000;

    /**
     * Fin de tour ou notification : l'état vient de changer, la lecture gardée (ou celle déjà partie)
     * le décrit d'avant. Les lectures suivantes repartent du serveur, et se partagent entre elles.
     */
    public static invalidateStartChecklist(communityId: string): void {
        this.startChecklistGeneration.set(communityId, (this.startChecklistGeneration.get(communityId) ?? 0) + 1);
        this.startChecklistCache.delete(communityId);
        this.startChecklistInFlight.delete(communityId);
    }

    public static async startChecklist(communityId: string, component?: INetworkComponent): Promise<ICommunityStartChecklistModel> {
        let cached = this.startChecklistCache.get(communityId);
        if (cached && Date.now() - cached.time < this.START_CHECKLIST_CACHE_MILLIS) {
            return cached.checklist;
        }
        let inFlight = this.startChecklistInFlight.get(communityId);
        if (inFlight) {
            return inFlight;
        }
        let generation = this.startChecklistGeneration.get(communityId) ?? 0;
        let fetching = (async () => {
            let request = await Network.get(ProductType.COMMUNITY, `/community/start/${communityId}`, component);
            let checklist = request.status == HttpStatus.OK && request.data ? request.data : undefined;
            // une lecture partie avant l'invalidation ne doit pas remettre l'état d'avant en cache
            if (checklist && generation == (this.startChecklistGeneration.get(communityId) ?? 0)) {
                this.startChecklistCache.set(communityId, {time: Date.now(), checklist: checklist});
            }
            return checklist;
        })();
        this.startChecklistInFlight.set(communityId, fetching);
        try {
            return await fetching;
        } finally {
            if (this.startChecklistInFlight.get(communityId) == fetching) {
                this.startChecklistInFlight.delete(communityId);
            }
        }
    }

    /**
     * Dit au serveur que le client vient d'ouvrir la page d'un de ses articles. Seule la première
     * ouverture est retenue côté serveur, l'appel est donc sans effet ensuite.
     */
    public static async startArticleSeen(communityId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/start/articleSeen/${communityId}`, {}, component);
        return request.status == HttpStatus.OK && request.data == true;
    }

    public static async startSkipSite(communityId: string, component?: INetworkComponent): Promise<ICommunityStartChecklistModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/start/skipSite/${communityId}`, {}, component);
        this.startChecklistCache.delete(communityId);
        return request.status == HttpStatus.OK && request.data ? request.data : undefined;
    }

    /**
     * Remet le parcours de démarrage au premier jour : fil archivé, articles et thématiques
     * supprimés, connexion du site défaite. Endpoint de test, réservé aux adresses internes par la
     * configuration de sécurité et jamais proposé en production.
     */
    public static async startReset(communityId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.post(ProductType.COMMUNITY, `/test/onboarding/reset/${communityId}`, {}, component);
        return request.status == HttpStatus.OK;
    }

    public static async updateContext(communityId: string, context: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/context/update`, {
            communityId: communityId,
            context: context
        }, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async createHost(communityId: string, originalUrl: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/originalHost/update`, {
            communityId: communityId,
            originalUrl: originalUrl
        }, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async findBySite(siteId: string, type: CommunityType, component?: INetworkComponent): Promise<ICommunityOverviewModel[]> {
        Services.handleErrors(component, [
            {status: HttpStatus.SERVICE_UNAVAILABLE, message: "none"},
        ]);

        let request = await Network.get(ProductType.COMMUNITY, `/community/site/${siteId}/${type}`, component);
        if (request && request.status == HttpStatus.OK) {
            return request.data;
        }

        return undefined;
    }

    public static async findLimitsBySite(siteId: string, component?: INetworkComponent): Promise<{
        [key: number]: number
    }> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/site/${siteId}/limits`, component);
        if (request && request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async delete(communityId: string, component?: INetworkComponent): Promise<void> {
        await Network.delete(ProductType.COMMUNITY, `/community/${communityId}`, component);
    }

    public static async findById(communityId: string, language: string, component?: INetworkComponent): Promise<ICommunityModel> {
        Services.handleErrors(component, [
            {status: HttpStatus.NOT_FOUND, message: "none"}
        ]);

        let request = await Network.get(ProductType.COMMUNITY, `/community/${communityId}/${language}`, component);
        if (request && request.status == HttpStatus.OK) {
            return this.store(request.data);
        }

        return undefined;
    }

    public static async createStep0(communityCreateStep0Model: ICommunityCreateStep0Model, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.postJson(ProductType.COMMUNITY, "/community/create/step/0", communityCreateStep0Model, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async createStep1(communityCreateStep1Model: ICommunityCreateStep1Model, component?: INetworkComponent): Promise<ICommunityModel> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.COMMUNITY_DUPLICATE, message: Resources.t("words.communityDuplicate")}
        ]);

        let request = await Network.postJson(ProductType.COMMUNITY, "/community/create/step/1", communityCreateStep1Model, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async configExportNone(communityId: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/config/export/none/${communityId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async addRedactor(communityId: string, name: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.post(ProductType.COMMUNITY, "/community/redactor", {
            communityId: communityId,
            name: name
        }, component);

        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }

        return undefined;
    }

    public static async deleteRedactor(communityId: string, redactorId: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.delete(ProductType.COMMUNITY, `/community/redactor/${communityId}/${redactorId}`, component);

        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }

        return undefined;
    }

    public static async checkInternalHost(communityId: string, hostname: string, communityType: CommunityType, component?: INetworkComponent): Promise<string> {
        Services.handleErrors(component, [
            {status: HttpStatus.BAD_REQUEST, errorCode: ErrorCode.HOST_EXIST, message: "none"},
            {status: HttpStatus.NOT_FOUND, errorCode: ErrorCode.HOST_EXIST}
        ]);

        let request = await Network.get(ProductType.COMMUNITY, `/community/checkInternalHost/${hostname}/${communityType}/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }

        return undefined;
    }

    public static async getById(communityId: string, component?: INetworkComponent): Promise<ICommunityModel> {
        Services.handleErrors(component, [
            {errorCode: ErrorCode.SUSPENDED, message: Resources.t("words.communitySuspendedDueToOfferInsufficiency")},
            {status: HttpStatus.NOT_FOUND, message: "none"},
        ]);
        let request = await Network.get(ProductType.COMMUNITY, `/community/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
    }

    public static async selectPartnerBlog(communityId: string, remoteId: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/select/partner/blog/${communityId}/${remoteId}`, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
    }

public static async selectPartnerCategory(communityId: string, remoteId: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/select/partner/category/${communityId}/${remoteId}`, {}, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async getPartnerBlogColumns(communityId: string, component?: INetworkComponent): Promise<ICommunityPartnerBlogColumnIncoming[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/list/partner/blog/columns/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async updatePartnerBlogColumns(communityId: string, columns: ICommunityPartnerBlogColumnOutgoing[], component?: INetworkComponent): Promise<ICommunityPartnerBlogColumnIncoming[]> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/list/partner/blog/columns/${communityId}`, columns, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getListPartnerBlog(communityId: string, component?: INetworkComponent): Promise<ICommunityPartnerBlogModel[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/list/partner/blog/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getListPartnerCategories(communityId: string, component?: INetworkComponent): Promise<ICommunityPartnerCategoryModel[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/list/partner/categories/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getMetafieldDefinitions(communityId: string, format: number, component?: INetworkComponent): Promise<ICommunityPartnerMetafieldDefinition[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/partner/metafield/definitions/${communityId}/${format}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getMetafieldConfig(communityId: string, format: number, component?: INetworkComponent): Promise<ICommunityPartnerMetafieldConfigIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/partner/metafield/config/${communityId}/${format}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async updateMetafieldConfig(communityId: string, config: ICommunityPartnerMetafieldConfigOutgoing, component?: INetworkComponent): Promise<ICommunityPartnerMetafieldConfigIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/partner/metafield/config/${communityId}`, config, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async updateJoomlaConfig(communityId: string, urlPattern: string, component?: INetworkComponent): Promise<string> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/partner/joomla/config/${communityId}`, {urlPattern: urlPattern}, component);
        if (request.status == HttpStatus.OK) {
            return request.data?.urlPattern;
        }
    }

    public static async getMetaobjectDefinitionFields(communityId: string, definitionId: string, component?: INetworkComponent): Promise<ICommunityPartnerMetaobjectField[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/partner/metaobject/fields/${communityId}?definitionId=${encodeURIComponent(definitionId)}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async resolveCommunityId(host: string, language: string, component?: INetworkComponent): Promise<string> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/resolve/${host}/${language}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async getCommunitiesAdmin(siteId: string, component?: INetworkComponent): Promise<ICommunityAdminModel[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/admin/${siteId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async updateAdmin(communityId: string, payload: ICommunityAdminUpdateOutgoing, component?: INetworkComponent): Promise<ICommunityAdminModel> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/admin/update/${communityId}`, payload, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async resetGscAdmin(communityId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/admin/${communityId}/resetGsc`, {}, component);
        return request.status == HttpStatus.OK;
    }

    public static async regenerateWritingStyleAdmin(communityId: string, type: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.post(ProductType.COMMUNITY, `/community/admin/${communityId}/writingStyle/regenerate/${type}`, {}, component);
        return request.status == HttpStatus.ACCEPTED;
    }

    public static async getCommunityAdminUsage(component?: INetworkComponent): Promise<{
        [outerKey: number]: { [innerKey: string]: number }
    }> {
        let request = await Network.get(ProductType.COMMUNITY, "/community/admin/usage", component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async resetCommunityStatsBySiteId(siteId: string, component?: INetworkComponent): Promise<boolean> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/admin/resetStats/${siteId}`, component);
        if (request.status == HttpStatus.OK) {
            return true;
        }
    }

    public static async updateSettings(communityId: string, settings: ICommunitySettings, component?: INetworkComponent): Promise<ICommunitySettings> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/${communityId}/settings`, settings, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async getSettings(communityId: string, component?: INetworkComponent): Promise<ICommunitySettingsResponse> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/${communityId}/settings`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return undefined;
    }

    public static async updateLimitsOverride(communityId: string, overrides: { [key: number]: number }, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/limits/override/${communityId}`, overrides, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async updateCustomLimits(communityId: string, customLimits: { [key: number]: number }, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/limits/custom/${communityId}`, customLimits, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async getAutopilotsBySiteIds(siteIds: string[], component?: INetworkComponent): Promise<{ [siteId: string]: boolean }> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/admin/autopilots/bySiteIds`, siteIds, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return {};
    }

    public static async getDemoRoi(communityIds: string[], component?: INetworkComponent): Promise<ICommunityDemoRoiModel[]> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/admin/demo/roi`, communityIds, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
        return [];
    }

    public static async shareCalendar(communityId: string, states: string[], component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/${communityId}/share/calendar`, {states: states}, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    public static async toggleShare(communityId: string, component?: INetworkComponent): Promise<ICommunityModel> {
        let request = await Network.putJson(ProductType.COMMUNITY, `/community/${communityId}/share`, {}, component);
        if (request.status == HttpStatus.OK) {
            return this.store(request.data);
        }
        return undefined;
    }

    /**
     * start
     */

    public static async start(options: ICommunityOptions, communityCreateStep0Model: ICommunityCreateStep0Model, component?: INetworkComponent): Promise<ICommunityModel> {
        let community = await this.createStep0(communityCreateStep0Model, component);
        if (community) {
            await Network.router.pages.reload(`/${options.basePath}/${community.id}/content/creation`);
        }
        return community;
    }

    /**
     * websocket
     */

    public static async startSocket(onRetry?: () => void): Promise<NetworkSocket> {
        let retry = async () => {
            if (onRetry) {
                onRetry();
            }

            await SedestralMachine.sleep(2500);

            if (await this.startSocket()) {
                await SedestralMachine.sleep(7000);
                window.location.reload();
            }
        }

        let socket = await Network.startWebSocket(ProductType.COMMUNITY, () => retry());
        if (!socket) {
            await retry();
        }

        return socket;
    }

    public static disposeSocket(): void {
        Network.getSocket(ProductType.COMMUNITY).disconnect(true);
    }

    public static async join(id: string) {
        if (id != null) {
            await Network.join(ProductType.COMMUNITY, id, WebSocketCommunityEventName.COMMUNITY_JOIN);
        }
    }


    public static leave(id: string) {
        Network.emit(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_LEAVE, id);
    }


    /**
     * store
     */

    public static storeAll(communities: ICommunityModel[]): ICommunityModel[] {
        for (let key in communities) {
            communities[key] = this.store(communities[key]);
        }
        return Services.storeAll(communities);
    }

    public static store(community: ICommunityModel): ICommunityModel {
        let storedCommunity = Services.get("id", this.communities, community.id);
        if (storedCommunity != undefined) {

            if (!community.partner && storedCommunity.partner) {
                community.partner = storedCommunity.partner;
            }

            if (!community.context && storedCommunity.context) {
                community.context = storedCommunity.context;
            }

            if (!community.redactors && storedCommunity.redactors) {
                community.redactors = storedCommunity.redactors;
            }
        }

        return Services.store("id", this.communities, community);
    }
}